/**
 * Two-list column picker for SO/PO Lines export templates.
 *
 * Left list = available columns (not yet selected). Right list = selected
 * columns in export order, reorderable via move up/down. Add/remove move
 * columns between the two lists. Kept deliberately simple (no drag-and-drop)
 * for robustness and lint-cleanliness.
 */
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { ChevronUp, ChevronDown, Plus, X } from 'lucide-react';
import { POLineExportColumn } from '../../../lib/api';

interface ExportTemplateColumnPickerProps {
  /** Full registry of available columns. */
  allColumns: POLineExportColumn[];
  /** Currently selected column keys, in export order. */
  selected: string[];
  onChange: (next: string[]) => void;
}

export function ExportTemplateColumnPicker({
  allColumns,
  selected,
  onChange,
}: ExportTemplateColumnPickerProps) {
  const labelFor = (key: string): string =>
    allColumns.find((c) => c.key === key)?.label ?? key;

  const available = allColumns.filter((c) => !selected.includes(c.key));

  const addColumn = (key: string) => {
    if (!selected.includes(key)) onChange([...selected, key]);
  };

  const removeColumn = (key: string) => {
    onChange(selected.filter((k) => k !== key));
  };

  const move = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= selected.length) return;
    const next = [...selected];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  return (
    <div className="grid grid-cols-2 gap-4">
      {/* Available columns */}
      <div className="flex flex-col">
        <Label className="mb-1">Available columns</Label>
        <div className="border border-input rounded-md h-64 overflow-y-auto divide-y">
          {available.length === 0 ? (
            <p className="text-sm text-muted-foreground p-3">All columns selected</p>
          ) : (
            available.map((col) => (
              <div
                key={col.key}
                className="flex items-center justify-between px-3 py-1.5 text-sm hover:bg-muted/50"
              >
                <span className="truncate">{col.label}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 shrink-0"
                  onClick={() => addColumn(col.key)}
                  aria-label={`Add ${col.label}`}
                >
                  <Plus className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Selected columns (ordered) */}
      <div className="flex flex-col">
        <Label className="mb-1">Selected columns ({selected.length})</Label>
        <div className="border border-input rounded-md h-64 overflow-y-auto divide-y">
          {selected.length === 0 ? (
            <p className="text-sm text-muted-foreground p-3">No columns selected yet</p>
          ) : (
            selected.map((key, index) => (
              <div
                key={key}
                className="flex items-center justify-between px-3 py-1.5 text-sm hover:bg-muted/50"
              >
                <span className="truncate flex items-center gap-2">
                  <span className="text-muted-foreground tabular-nums w-5 text-right">
                    {index + 1}.
                  </span>
                  {labelFor(key)}
                </span>
                <div className="flex items-center gap-0.5 shrink-0">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    disabled={index === 0}
                    onClick={() => move(index, -1)}
                    aria-label={`Move ${labelFor(key)} up`}
                  >
                    <ChevronUp className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    disabled={index === selected.length - 1}
                    onClick={() => move(index, 1)}
                    aria-label={`Move ${labelFor(key)} down`}
                  >
                    <ChevronDown className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 text-destructive"
                    onClick={() => removeColumn(key)}
                    aria-label={`Remove ${labelFor(key)}`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
